// app/features/templates/services_and_hooks/useTemplatesHook.ts
// v2

// app/features/templates/services_and_hooks/useTemplatesHook.ts
"use client";

import { useQueryState } from "nuqs";
import { useMemo } from "react";

import { TemplateFilterState } from "../templates.interface";
import {
  useDownloadFileMutation,
  useGetTemplateBySlugQuery,
  useGetTemplatesQuery,
} from "./useTemplatesQueryHook";

// --- URL State Management Hook (No changes needed) ---
export const useTemplateFilters = () => {
  const [searchTerm, setSearchTerm] = useQueryState("search", {
    defaultValue: "",
  });
  const [category, setCategory] = useQueryState("category", {
    defaultValue: "",
  });
  const [tag, setTag] = useQueryState("tag", { defaultValue: "" });
  const [freebies, setFreebies] = useQueryState("freebies", {
    defaultValue: "",
  });

  const filters: TemplateFilterState = useMemo(
    () => ({
      searchTerm: searchTerm || undefined,
      category: category && category !== "all" ? category : undefined,
      tag: tag || undefined,
      freebies: freebies ? freebies === "true" : undefined,
      page: 1,
      sortBy: "newest",
    }),
    [searchTerm, category, tag, freebies],
  );

  const setFilters = (newFilters: Partial<TemplateFilterState>) => {
    setSearchTerm(newFilters.searchTerm ?? "");
    setCategory(newFilters.category ?? "");
    setTag(newFilters.tag ?? "");
    setFreebies(newFilters.freebies ? "true" : "");
  };

  const resetFilters = () => {
    setSearchTerm("");
    setCategory("");
    setTag("");
    setFreebies("");
  };

  return {
    filters,
    setFilters,
    resetFilters,
    searchTerm,
    setSearchTerm,
    category,
    setCategory,
    tag,
    setTag,
    freebies: freebies === "true",
    setFreebies: (value: boolean) => setFreebies(value ? "true" : ""),
  };
};

// --- Unified Hook for Template Listing (No changes needed) ---
export const useTemplatesListing = (itemsPerPage: number = 10) => {
  const { filters } = useTemplateFilters();
  const queryFilters = { ...filters, limit: itemsPerPage };
  const { templates, totalCount, ...rest } = useGetTemplatesQuery({
    filters: queryFilters,
  });

  return { templates, totalCount, ...rest };
};

// --- [CORRECTED] Unified Hook for Template Detail ---
export const useTemplateDetail = (slug: string | null) => {
  const { data: template, ...query } = useGetTemplateBySlugQuery(slug);

  // Use the new, correct mutation hook.
  const downloadMutation = useDownloadFileMutation();

  /**
   * Initiates a download for a specific file.
   * The actual download and error handling logic is encapsulated in the mutation hook.
   * @param fileId The UUID of the file to download.
   */
  const handleFileDownload = (fileId: string) => {
    if (!fileId) {
      console.error("handleFileDownload: fileId is required.");
      return;
    }
    // This simply triggers the mutation. The `onSuccess` and `onError` callbacks
    // inside `useDownloadFileMutation` will handle the rest.
    downloadMutation.mutate(fileId);
  };

  return {
    template,
    ...query,
    // Renamed for clarity and correctness
    handleFileDownload,
    // The loading state is now tied to the file download mutation
    isDownloading: downloadMutation.isPending,
  };
};

// // app/features/templates/services_and_hooks/useTemplatesHook.ts
// "use client";

// import { useQueryState } from 'nuqs';
// import { useMemo } from 'react';

// import { TemplateFilterState } from '../templates.interface';
// import { useGetTemplateBySlugQuery, useGetTemplateDownloadUrlMutation, useGetTemplatesQuery } from './useTemplatesQueryHook';
// // Assuming showToast exists, e.g., from a shared provider. If not, this part can be commented out or adapted.
// // import { showToast } from '@/shared/providers/ReactQueryProvider'; // Example path

// // --- URL State Management Hook (following system blueprint) ---
// export const useTemplateFilters = () => {
//   // Using nuqs for URL state management as specified in system blueprint
//   const [searchTerm, setSearchTerm] = useQueryState('search', {
//     defaultValue: '',
//     clearOnDefault: true,
//   });

//   const [category, setCategory] = useQueryState('category', {
//     defaultValue: '',
//     clearOnDefault: true,
//   });

//   const [tag, setTag] = useQueryState('tag', {
//     defaultValue: '',
//     clearOnDefault: true,
//   });

//   const [freebies, setFreebies] = useQueryState('freebies', {
//     defaultValue: '',
//     clearOnDefault: true,
//   });

//   // Convert URL state to filter object
//   const filters: TemplateFilterState = useMemo(() => ({
//     searchTerm: searchTerm || undefined,
//     category: category || undefined,
//     tag: tag || undefined,
//     freebies: freebies ? freebies === 'true' : undefined,
//     page: 1, // Always start at page 1 for infinite query
//     sortBy: 'newest',
//   }), [searchTerm, category, tag, freebies]);

//   const setFilters = (newFilters: Partial<TemplateFilterState>) => {
//     if (newFilters.searchTerm !== undefined) {
//       setSearchTerm(newFilters.searchTerm || '');
//     }
//     if (newFilters.category !== undefined) {
//       setCategory(newFilters.category || '');
//     }
//     if (newFilters.tag !== undefined) {
//       setTag(newFilters.tag || '');
//     }
//     if (newFilters.freebies !== undefined) {
//       setFreebies(newFilters.freebies ? 'true' : '');
//     }
//   };

//   const resetFilters = () => {
//     setSearchTerm('');
//     setCategory('');
//     setTag('');
//     setFreebies('');
//   };

//   return {
//     filters,
//     setFilters,
//     resetFilters,
//     // Individual filter setters for direct use in components
//     searchTerm,
//     setSearchTerm,
//     category,
//     setCategory,
//     tag,
//     setTag,
//     freebies: freebies === 'true',
//     setFreebies: (value: boolean) => setFreebies(value ? 'true' : ''),
//   };
// };

// // --- Unified Hook for Template Listing ---
// export const useTemplatesListing = (itemsPerPage: number = 10) => {
//   const { filters } = useTemplateFilters();

//   // Pass the limit from itemsPerPage to the query hook's filters
//   const queryFilters = { ...filters, limit: itemsPerPage };

//   const {
//     data, // This is the raw paginated data from useInfiniteQuery
//     fetchNextPage,
//     hasNextPage,
//     isFetchingNextPage,
//     isLoading,
//     isError,
//     error,
//     refetch, // refetch function from useInfiniteQuery
//     templates, // This is the flattened list added in useTemplatesQueryHook
//     totalCount, // This is the total count from the first page
//   } = useGetTemplatesQuery({ filters: queryFilters });

//   // The templates and totalCount are already conveniently provided by useGetTemplatesQuery

//   return {
//     templates, // Flattened list of all fetched templates
//     totalCount,
//     isLoading,
//     isError,
//     error,
//     fetchNextPage,
//     hasNextPage,
//     isFetchingNextPage,
//     refetchTemplates: refetch, // Expose refetch
//   };
// };

// // --- Unified Hook for Template Detail ---
// export const useTemplateDetail = (slug: string | null) => {
//   const {
//     data: template,
//     isLoading,
//     isError,
//     error,
//     refetch
//   } = useGetTemplateBySlugQuery(slug);

//   const downloadMutation = useGetTemplateDownloadUrlMutation();

//   const handleDownload = async (templateId: string, templateTitle?: string) => {
//     if (!templateId) {
//       console.error("handleDownload: templateId is required.");
//       // showToast?.('error', 'Download Error', 'Template ID is missing.', 5000);
//       return;
//     }
//     try {
//       const { downloadUrl, fileName } = await downloadMutation.mutateAsync(templateId);

//       const link = document.createElement('a');
//       link.href = downloadUrl;
//       // Use the filename from the API, or derive from title, or a default
//       link.download = fileName || templateTitle?.replace(/[^a-z0-9_\-.]/gi, '_') || 'template_download';
//       document.body.appendChild(link);
//       link.click();
//       document.body.removeChild(link);

//       // showToast?.('success', 'Download Started', `Downloading ${templateTitle || 'template'}...\`, 3000);

//     } catch (err: any) {
//       console.error("Download error:", err);
//       // showToast?.('error', 'Download Failed', err.message || 'Could not initiate download.', 5000);
//     }
//   };

//   return {
//     template,
//     isLoading,
//     isError,
//     error,
//     refetchTemplate: refetch,
//     handleDownload,
//     isDownloading: downloadMutation.isPending,
//   };
// };
